import React, { useState, useRef, useEffect } from 'react';
import "./Order.css";

import emailjs from '@emailjs/browser';

import Title from "../../shared/ui/Title/title";

function Order({ data }) {
  console.log('Order data', data);

  const initialOrderValue = data ? [data.name, "Ø" + data.size, data.gramm + "г", data.price + " руб."].join(" • ") : '';

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: initialOrderValue
  });
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData(prev => ({
        ...prev,
        message: prev.message 
          ? prev.message + "\n" + [data.name, "Ø" + data.size, data.gramm + "г", data.price + " руб."].join(" • ")
          : [data.name, "Ø" + data.size, data.gramm + "г", data.price + " руб."].join(" • ")
      }));
    }
  }, [data]);

  const form = useRef();

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value) return 'Имя обязательно';
        if (value.length < 2) return 'Имя должно содержать минимум 2 символа';
        if (!/^[a-zA-Zа-яА-ЯёЁ\s-]+$/.test(value)) return 'Имя может содержать только буквы';
        return '';
      case 'phone':
        if (!value) return 'Телефон обязателен';
        const phoneDigits = value.replace(/\D/g, '');
        if (phoneDigits.length < 10) return 'Введите корректный номер телефона';
        return '';
      case 'message':
        if (!value && !data) return 'Добавьте товар в заказ';
        return '';
      case 'consent':
        if (!value) return 'Необходимо согласие на обработку персональных данных';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Валидация при изменении
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Валидация при потере фокуса
    const error = validateField(name, formData[name]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    
    // Форматирование номера
    if (value.length > 0) {
      if (value.length <= 1) {
        value = `+7 (${value}`;
      } else if (value.length <= 4) {
        value = `+7 (${value.slice(1)}`;
      } else if (value.length <= 7) {
        value = `+7 (${value.slice(1, 4)}) ${value.slice(4)}`;
      } else if (value.length <= 9) {
        value = `+7 (${value.slice(1, 4)}) ${value.slice(4, 7)}-${value.slice(7)}`;
      } else {
        value = `+7 (${value.slice(1, 4)}) ${value.slice(4, 7)}-${value.slice(7, 9)}-${value.slice(9, 11)}`;
      }
    } else {
      value = '';
    }
    
    setFormData(prev => ({ ...prev, phone: value }));
    
    const error = validateField('phone', value);
    setErrors(prev => ({ ...prev, phone: error }));
  };

  const handleConsentChange = (e) => {
    setIsConsentChecked(e.target.checked);
    const error = validateField('consent', e.target.checked);
    setErrors(prev => ({ ...prev, consent: error }));
    setTouched(prev => ({ ...prev, consent: true }));
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    // Валидация всех полей
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    
    // Валидация согласия
    const consentError = validateField('consent', isConsentChecked);
    if (consentError) newErrors.consent = consentError;

    setErrors(newErrors);
    setTouched({ name: true, phone: true, message: true, consent: true });

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      await emailjs.sendForm(
        'service_mc5zbyl', 
        'template_iy79fc9', 
        form.current, 
        'Sh9n2H9hAJA-6DyLW'
      );
      
      setIsSuccess(true);
      setFormData({ name: '', phone: '', message: initialOrderValue });
      setIsConsentChecked(false);
      setTouched({});
      
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      setIsError(true);
      setTimeout(() => setIsError(false), 5000);
      console.log('FAILED...', error.text);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return formData.name && formData.phone && (formData.message || data) && 
           isConsentChecked &&
           !Object.values(errors).some(error => error);
  };

  return (
    <section className="order" id="Order">
      <Title title="Заказать" translate="Order" />
      
      <div className="order-container">
        <div className="order-content">
          {/* Левая часть с информацией */}
          <div className="order-info">
            <div className="info-badge">
              <span className="badge-icon">🎂</span>
              <span className="badge-text">Индивидуальный заказ</span>
            </div>
            
            <h3 className="info-title">
              Создайте свой <span className="title-accent">идеальный десерт</span>
            </h3>
            
            <p className="info-text">
              Расскажите о своих пожеланиях, и я помогу подобрать идеальный вариант 
              для вашего праздника
            </p>

            <div className="info-features">
              <div className="info-feature">
                <span className="feature-check">✓</span>
                <span>Бесплатная консультация</span>
              </div>
              <div className="info-feature">
                <span className="feature-check">✓</span>
                <span>Индивидуальный дизайн</span>
              </div>
              <div className="info-feature">
                <span className="feature-check">✓</span>
                <span>Свежие ингредиенты</span>
              </div>
            </div>

            <div className="info-contact">
              <p className="contact-label">Или свяжитесь напрямую:</p>
              <a href="https://t.me/militr_am" className="contact-link" target="_blank" rel="noopener noreferrer">
                <span className="contact-icon">✈️</span>
                Telegram
              </a>
            </div>
          </div>

          {/* Правая часть с формой */}
          <div className="order-form-wrapper">
            <form className="order-form" ref={form} onSubmit={sendEmail}>
              <div className="form-header">
                <h4 className="form-title">Оформить заказ</h4>
                <p className="form-subtitle">Заполните форму, и я свяжусь с вами</p>
              </div>

              <div className="form-fields">
                {/* Поле имени */}
                <div className={`form-field ${touched.name && errors.name ? 'error' : ''} ${touched.name && !errors.name && formData.name ? 'success' : ''}`}>
                  <label className="field-label">
                    <span className="label-icon">👤</span>
                    <span className="label-text">Ваше имя</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Введите ваше имя"
                    className="field-input"
                  />
                  {touched.name && errors.name && (
                    <span className="field-error">{errors.name}</span>
                  )}
                </div>

                {/* Поле телефона */}
                <div className={`form-field ${touched.phone && errors.phone ? 'error' : ''} ${touched.phone && !errors.phone && formData.phone ? 'success' : ''}`}>
                  <label className="field-label">
                    <span className="label-icon">📞</span>
                    <span className="label-text">Телефон</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    onBlur={handleBlur}
                    placeholder="+7 (___) ___-__-__"
                    className="field-input"
                  />
                  {touched.phone && errors.phone && (
                    <span className="field-error">{errors.phone}</span>
                  )}
                </div>

                {/* Поле заказа */}
                <div className={`form-field ${touched.message && errors.message ? 'error' : ''}`}>
                  <label className="field-label">
                    <span className="label-icon">📝</span>
                    <span className="label-text">Детали заказа</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Опишите ваш заказ или выберите товар выше"
                    rows="5"
                    className="field-input"
                  />
                  {touched.message && errors.message && (
                    <span className="field-error">{errors.message}</span>
                  )}
                </div>

                {/* Выбранные товары */}
                {data && (
                  <div className="selected-products">
                    <span className="products-label">Выбранные товары:</span>
                    <div className="product-tag">
                      <span className="product-name">{data.name}</span>
                      <span className="product-details">
                        {data.size && `Ø${data.size} • `}
                        {data.gramm && `${data.gramm}г • `}
                        {data.price && `${data.price}₽`}
                      </span>
                    </div>
                  </div>
                )}

                {/* Согласие на обработку персональных данных */}
                <div className={`consent-field ${touched.consent && errors.consent ? 'error' : ''}`}>
                  <label className="consent-label">
                    <input
                      type="checkbox"
                      checked={isConsentChecked}
                      onChange={handleConsentChange}
                      className="consent-checkbox"
                    />
                    <span className="consent-text">
                      Я соглашаюсь на{' '}
                      <a href="/policy" target="_blank" rel="noopener noreferrer" className="consent-link">
                        обработку персональных данных
                      </a>
                    </span>
                  </label>
                  {touched.consent && errors.consent && (
                    <span className="field-error">{errors.consent}</span>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
                  disabled={!isFormValid() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      <span>Отправка...</span>
                    </>
                  ) : (
                    <>
                      <span>Заказать</span>
                      <span className="button-arrow">→</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Попапы */}
        {isSuccess && (
          <div className="popup success">
            <div className="popup-icon">✓</div>
            <div className="popup-content">
              <h4>Заказ отправлен!</h4>
              <p>Я свяжусь с вами в ближайшее время</p>
            </div>
          </div>
        )}

        {isError && (
          <div className="popup error">
            <div className="popup-icon">✗</div>
            <div className="popup-content">
              <h4>Ошибка отправки</h4>
              <p>Попробуйте позже или свяжитесь напрямую</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default Order;